// 004 (FR-024, FR-025, FR-045): remove a child after confirmation. On success the list is re-read,
// 003's students probe is reset (its no-children gate applies again) and a notice is shown. A
// refusal keeps the child and returns the reason (open order).
import { useState } from "react";

import { isAppError } from "@/api/errors";
import { studentsProbe } from "@/join/studentsProbe";
import { appNotices } from "@/shell/notice/store";
import { ConfirmDialog } from "@/ui/ConfirmDialog";

import { PROFILE_COPY, profileErrorMessage } from "../copy";
import { useParentData } from "../useParentData";

export function useRemoveChild(onRemoved?: () => void) {
  const { actions } = useParentData();
  const [target, setTarget] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<{ id: string; text: string } | null>(null);

  async function confirm() {
    if (!target || pending) return;
    setPending(true);
    try {
      await actions.removeStudent(target.id);
      studentsProbe.reset();
      appNotices.post(PROFILE_COPY.childRemoved(target.name));
      setTarget(null);
      onRemoved?.();
    } catch (e) {
      const text = isAppError(e)
        ? (profileErrorMessage(e, { action: "remove", child: target.name })
            .form ?? "")
        : "";
      setError({ id: target.id, text });
      setTarget(null);
    } finally {
      setPending(false);
    }
  }

  const dialog = (
    <ConfirmDialog
      visible={target !== null}
      title={target ? PROFILE_COPY.removeConfirm(target.name) : ""}
      confirmLabel={PROFILE_COPY.remove}
      cancelLabel={PROFILE_COPY.cancel}
      onConfirm={() => void confirm()}
      onCancel={() => setTarget(null)}
      pending={pending}
    />
  );

  return {
    ask: (child: { id: string; name: string }) => {
      setError(null);
      setTarget(child);
    },
    error,
    dialog,
  };
}
